import React from 'react';
import {
  AdminIcon,
  ChatIcon,
  ContactIcon,
  DashboardIcon,
  DemoIcon,
  ImpersonateIcon,
  LogoutIcon,
  PeopleIcon,
  SettingsIcon,
} from '../../icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../../context/AdminAuthContext';
import { useAdminPermissions } from '../../../hooks/useAdminPermissions';
import { ADMIN_PERMISSIONS } from '../../../constants/permissions';
import { ADMIN_PATHS, adminPath, normalizeAdminRoutePath } from '../../../constants/adminPaths';
import { useSupportTicketCount } from '../../dashboard/widgets/useSupportTicketCount';
import { useControlPanelBranding } from '../../../context/control-panel/ControlPanelBrandingContext';
import { ADMIN_SIDEBAR_ITEMS, buildAdminSidebarSections } from './adminSidebarNav';
import AdminSidebarThemeToggle from './AdminSidebarThemeToggle';

const MENU_ICONS = {
  [ADMIN_PATHS.DASHBOARD]: DashboardIcon,
  [ADMIN_PATHS.DEMOS]: DemoIcon,
  [ADMIN_PATHS.IMPERSONATION]: ImpersonateIcon,
  [ADMIN_PATHS.USERS]: PeopleIcon,
  [ADMIN_PATHS.SUPPORT]: ChatIcon,
  [ADMIN_PATHS.CONTACT_SUBMISSIONS]: ContactIcon,
  [ADMIN_PATHS.SETTINGS]: SettingsIcon,
  [ADMIN_PATHS.TOOLS]: AdminIcon,
};

function formatDisplayName(admin) {
  const firstName = admin?.firstName || '';
  const lastName = admin?.lastName || '';

  if (firstName && lastName) return `${firstName} ${lastName}`;
  if (firstName) return firstName;
  if (lastName) return lastName;
  return admin?.email || 'Admin User';
}

function formatDisplayRole(admin) {
  const rawRole = admin?.title || admin?.role || 'ADMIN';

  return rawRole
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export default function AdminSidebarContent({ onClose, showClose = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { admin, signOut } = useAdminAuth();
  const { name: controlPanelName } = useControlPanelBranding();
  const { hasPermission } = useAdminPermissions();
  const { count: openTicketCount } = useSupportTicketCount('open', { pollMs: 120000 });
  const supportBadgeText = typeof openTicketCount === 'number' && openTicketCount > 0
    ? (openTicketCount > 99 ? '99+' : String(openTicketCount))
    : null;

  const isSupportRole = admin?.role === 'support'
    || (hasPermission(ADMIN_PERMISSIONS.SUPPORT.VIEW) && !hasPermission(ADMIN_PERMISSIONS.DASHBOARD.VIEW));
  const panelTitle = isSupportRole ? 'Support Panel' : 'Admin Panel';

  const visibleItems = ADMIN_SIDEBAR_ITEMS.filter((item) => !item.permission || hasPermission(item.permission));
  const sections = buildAdminSidebarSections(visibleItems, isSupportRole);

  const handleNavigate = (path) => {
    navigate(adminPath(path));
    onClose?.();
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate(adminPath(ADMIN_PATHS.LOGIN));
      onClose?.();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <div className="admin-sidebar">
      <div className="admin-sidebar-brand">
        <div className="admin-sidebar-brand-text">
          <strong>{controlPanelName}</strong>
          <span>{panelTitle}</span>
        </div>
        {showClose ? (
          <button
            type="button"
            className="admin-sidebar-close"
            aria-label="Close menu"
            onClick={onClose}
          >
            ×
          </button>
        ) : null}
      </div>

      <nav className="admin-sidebar-nav" aria-label="Admin">
        {sections.map((section) => (
          <div key={section.label} className="admin-sidebar-nav-section">
            <div className="admin-sidebar-nav-label">{section.label}</div>
            <div className="admin-sidebar-nav-section-items">
              {section.items.map((item) => {
                const Icon = MENU_ICONS[item.path];
                const isActive = normalizeAdminRoutePath(location.pathname) === item.path;
                const showBadge = item.path === ADMIN_PATHS.SUPPORT && supportBadgeText;

                return (
                  <button
                    key={item.path}
                    type="button"
                    className={`admin-sidebar-nav-link${isActive ? ' is-active' : ''}`}
                    onClick={() => handleNavigate(item.path)}
                  >
                    {Icon ? (
                      <span className="admin-sidebar-nav-icon" aria-hidden="true">
                        <Icon />
                      </span>
                    ) : null}
                    <span className="admin-sidebar-nav-content">
                      <span className="admin-sidebar-nav-text">{item.text}</span>
                      {showBadge ? (
                        <span
                          className="admin-sidebar-nav-badge"
                          aria-label={`${supportBadgeText} open tickets`}
                        >
                          {supportBadgeText}
                        </span>
                      ) : null}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <div className="admin-sidebar-footer">
        <div className="admin-sidebar-account">
          <div className="admin-sidebar-account-user">
            <span className="admin-sidebar-account-name">{formatDisplayName(admin)}</span>
            <span className="admin-sidebar-account-role">{formatDisplayRole(admin)}</span>
          </div>
          <AdminSidebarThemeToggle />
        </div>

        <button
          type="button"
          className="admin-sidebar-nav-link admin-sidebar-nav-link-button"
          onClick={handleLogout}
        >
          <span className="admin-sidebar-nav-icon" aria-hidden="true">
            <LogoutIcon />
          </span>
          <span className="admin-sidebar-nav-text">Log Out</span>
        </button>
      </div>
    </div>
  );
}
